import { UntypedFormArray, UntypedFormBuilder } from "@angular/forms";
import moment from "moment";
import { CONSTANT, RATING_FORM_UI_VERSION_TS1_V3 } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import {
	CandidateSessionConfiguration,
	CandidateSignOffDetails,
	DraftItem,
	ManeuverOutcomeDraft,
	OBComments,
	RatingCandidateForm,
	TemCompetencyOutcomeDraft,
} from "src/app/models/models";
import {
	IObservationRating,
	IPreviewDisabledObservation,
} from "src/app/cbta-web/services/offline-rating-calculation.service";
import { OfflineRatingV2Calculator } from "src/app/cbta-web/services/offline-rating-calculation-v2.service";
import { OfflineRatingV4Calculator } from "src/app/cbta-web/ratings/container-ratings-form/ts1-rating-form-v4";
import { resolveOriginalObsCodesFromAssessmentConfig } from "src/app/cbta-web/ratings/container-ratings-form/ts1-rating-form-v4/calculation/original-obs-codes.utils";
import { resolveTs1RatingCalcKey } from "src/app/cbta-web/ratings/container-ratings-form/ts1-rating-form.registry";
import { buildFlightSimulatorDetailsPayload } from "../ratings/container-ratings-form/shared-v2/flight-simulator-details.payload";
import {
	createCandidateSignoffForm,
	createSignOffFormContainer,
} from "../ratings/container-ratings-form/shared-v2/base/form.factories";
import {
	buildLastObResetTimeMap,
	filterAddRemoveObForRating,
} from "../ratings/container-ratings-form/shared-v2/utils/observation.utils";
import {
	buildTs1V3FullyDisabledObKeys,
	buildTs1V3NaRoleObKeys,
	includeTs1V3ObInRatingNewObs,
	isTs1V3ConfigObsExcludedByNa,
	mergeTs1V3ObEventsForRatingCalc,
} from "../ratings/container-ratings-form/ts1-rating-form-v3/utils/ts1-rating-ab-ob-merge.utils";
import {
	buildTs1V3ManeuverCodesToExclude,
} from "../ratings/container-ratings-form/ts1-rating-form-v3/utils/ts1-rating-calc-preview.utils";
import { Ts1PreviewCandidateDetails } from "../ratings/container-ratings-form/ts1-rating-form-v3";
import {
	buildTs1ApplicableCountsFromAddRemoveOb,
	buildTs1ReviewCompetencyBlocks,
	buildTs1ReviewManeuverRows,
	normalizeTemCompetencyOutcomes,
	TS1_REVIEW_COMPETENCY_ORDER,
	Ts1ReviewCompetencyBlock,
	Ts1ReviewManeuverRow,
} from "../ratings/container-ratings-form/ts1-rating-form-v3";
import {
	inferCrewRoleForCandidateFromSelectedOBs,
	restoreV3OutcomesFromSelectedOBs,
} from "../ratings/container-ratings-form/ts1-rating-form-v3";

const previewCalculatorV3 = new OfflineRatingV2Calculator({
	formVersion: RATING_FORM_UI_VERSION_TS1_V3,
});
const previewCalculatorV4 = new OfflineRatingV4Calculator();

function resolveStagingPreviewCalculator(ratingFormVersion?: number) {
	switch (resolveTs1RatingCalcKey(ratingFormVersion)) {
		case "v4":
			return previewCalculatorV4;
		default:
			return previewCalculatorV3;
	}
}

export interface ChecklistInstructorSignoffView {
	name: string;
	licenseNumber: string;
	signatureDataUrl: string;
	signedAt: string;
}

export interface ChecklistStagingDetail {
	trainingAssessmentId: string;
	candidateId: string;
	draftCandidateId?: string;
	candidateIndex: number;
	instructorName: string;
	assessment?: DraftItem & {
		signOffMode?: string;
		remoteSignOff?: {
			instructorName?: string;
			instructorLicenseNumber?: string;
			instructorSignatureBase64?: string;
			instructorSignedAt?: string;
		};
		candidateRating?: DraftItem["candidateRating"] & {
			crewRoleAssignment?: {
				pfCandidateId?: string;
				pmCandidateId?: string;
				rolesByCandidateIndex?: CandidateRole[];
			};
			maneuverOutcomes?: ManeuverOutcomeDraft[];
			temCompetencyOutcomes?: TemCompetencyOutcomeDraft[];
		};
	};
	remoteSignOff?: {
		instructorName?: string;
		instructorLicenseNumber?: string;
		instructorSignatureBase64?: string;
		instructorSignedAt?: string;
	};
	signOffDetail?: CandidateSignOffDetails | Record<string, unknown> | null;
}

function formatChecklistSignedAt(value: unknown): string {
	if (!value) return "";
	const m = moment(value);
	return m.isValid() ? m.format("DD/MM/YY HH:mm:ss") : "";
}

export function buildChecklistInstructorSignoff(detail: ChecklistStagingDetail): ChecklistInstructorSignoffView {
	const assessment = detail.assessment;
	const remote = detail.remoteSignOff ?? assessment?.remoteSignOff;
	const candidateKey = detail.draftCandidateId ?? detail.candidateId;
	const candidateSignOff =
		assessment?.signOffDetails?.candidateSignOffDetails?.[candidateKey] ??
		detail.signOffDetail ??
		{};
	const signatureBase64 = remote?.instructorSignatureBase64 ?? "";
	const signatureDataUrl = signatureBase64
		? signatureBase64.startsWith("data:")
			? signatureBase64
			: `data:image/png;base64,${signatureBase64}`
		: "";

	return {
		name: remote?.instructorName ?? detail.instructorName ?? "—",
		licenseNumber:
			remote?.instructorLicenseNumber ??
			(candidateSignOff as { instructorLicenseNumber?: string }).instructorLicenseNumber ??
			"—",
		signatureDataUrl,
		signedAt: formatChecklistSignedAt(remote?.instructorSignedAt),
	};
}

function buildSessionConfigurationsFromStagingCandidate(
	candidate: RatingCandidateForm,
	formSessionDetails: Array<{ code: string; description: string }> = [],
): CandidateSessionConfiguration[] {
	const stored = Array.isArray(candidate.sessionDetails)
		? candidate.sessionDetails[0]
		: (candidate.sessionDetails ?? {});
	const remarksStored = Array.isArray(candidate.sessionRemarks)
		? candidate.sessionRemarks[0]
		: (candidate.sessionRemarks ?? {});
	const configs: CandidateSessionConfiguration[] = [];

	for (const [sectionCode, value] of Object.entries(stored as Record<string, unknown>)) {
		const codes = Array.isArray(value)
			? (value as string[])
			: Object.entries(value as Record<string, boolean>)
					.filter(([, selected]) => selected === true)
					.map(([code]) => code);
		const remarksSection = (remarksStored as Record<string, Record<string, string>>)?.[sectionCode] ?? {};

		for (const code of codes) {
			const match = formSessionDetails.find((s) => s.code === code);
			const remark =
				remarksSection[code] ??
				remarksSection[code.replace(/\//g, "_")] ??
				remarksSection[code.replace(/_/g, "/")] ??
				"";
			configs.push({
				description: match?.description ?? code,
				selectionRemarks: remark || undefined,
			});
		}
	}

	return configs;
}

function asStagingCandidates(candidates: unknown[]): RatingCandidateForm[] {
	return candidates as RatingCandidateForm[];
}

function asCrewRoleCandidates(candidates: RatingCandidateForm[]): Array<{ candidateId: string }> {
	return candidates
		.filter((c): c is RatingCandidateForm & { candidateId: string } => !!c.candidateId)
		.map((c) => ({ candidateId: c.candidateId }));
}

function resolveStagingCandidate(
	candidates: RatingCandidateForm[],
	detail: ChecklistStagingDetail,
): { candidate: RatingCandidateForm | undefined; candidateIndex: number } {
	const byIdIndex = candidates.findIndex((c) => c.candidateId === detail.candidateId);
	const candidateIndex = byIdIndex >= 0 ? byIdIndex : detail.candidateIndex;
	return {
		candidate: candidates[candidateIndex],
		candidateIndex,
	};
}

function displayOption(val: unknown): string {
	if (val == null || val === "") return "-";
	if (typeof val === "string") return val;
	if (typeof val === "object" && val !== null) {
		const o = val as { description?: string; code?: string };
		return (o.description ?? o.code ?? "-").toString();
	}
	return String(val);
}

function formatPreviewDate(value: unknown, format = "DD MMMM YYYY"): string {
	if (!value) return "-";
	const m = moment(value);
	return m.isValid() ? m.format(format) : "-";
}

function toRecommendationBool(value: unknown): boolean | undefined {
	if (value === true || value === "true" || value === "Yes" || value === "yes") return true;
	if (value === false || value === "false" || value === "No" || value === "no") return false;
	return undefined;
}

import {
  resolveCrewRolesForCandidates,
  resolveCrewRolesForCandidates as resolveCrewRolesFromStaging,
} from "../ratings/container-ratings-form/ts1-rating-form-v3/utils/ts1-crew-role.utils";

export { resolveCrewRolesForCandidates as resolveCrewRolesFromStaging };

function resolveStagingCrewRoles(
	candidates: RatingCandidateForm[],
	crewRoleAssignment:
		| {
				pfCandidateId?: string;
				pmCandidateId?: string;
				rolesByCandidateIndex?: CandidateRole[];
		  }
		| undefined,
	selectedOBs: OBComments[],
): CandidateRole[] {
	const baseRoles = resolveCrewRolesFromStaging(asCrewRoleCandidates(candidates), crewRoleAssignment);
	if (crewRoleAssignment?.rolesByCandidateIndex?.length) {
		return baseRoles;
	}

	return candidates.map((candidate, index) => {
		const inferred = inferCrewRoleForCandidateFromSelectedOBs(candidate.candidateId ?? "", selectedOBs);
		return inferred ?? baseRoles[index] ?? (index === 0 ? CandidateRole.PF : CandidateRole.PM);
	});
}

function computeStagingRadarMetrics(
	assessment: DraftItem,
	candidateId: string,
	options?: {
		candidateIndex?: number;
		hasSecondCandidate?: boolean;
		preferredRole?: CandidateRole;
		maneuverOutcomes?: ManeuverOutcomeDraft[];
	},
) {
	const maneuvers = assessment.formDetails?.assessmentConfig ?? [];
	const rates = assessment.candidateRating?.temRate ?? {};
	const addRemoveOb = assessment.candidateRating?.addRemoveOb ?? [];
	const resetManeuvers = assessment.candidateRating?.resetManeuvers ?? [];
	const candidateIndex = options?.candidateIndex ?? 0;
	const hasSecondCandidate = options?.hasSecondCandidate ?? false;
	const preferredRole =
		options?.preferredRole ?? (candidateIndex === 0 ? CandidateRole.PF : CandidateRole.PM);
	const maneuverOutcomes =
		options?.maneuverOutcomes ??
		(assessment.candidateRating?.maneuverOutcomes as ManeuverOutcomeDraft[] | undefined) ??
		[];

	const candidateAddRemoveOb =
		addRemoveOb
			.filter((d) => !!d?.observationCode && !!d?.maneuverCode)
			.map((d) => ({
			candidateId: d.candidateId,
			observationCode: d.observationCode,
			maneuverCode: d.maneuverCode,
			isRemoved: d.isRemoved,
			isDisabled: d.isDisabled,
			isRepeat: d.isRepeat ?? false,
			isReset: d.isReset ?? false,
			createdAt: d.createdAt,
			totalAttempt: d.totalAttempt,
			activeAttempt: d.activeAttempt,
			candidateRole: d.candidateRole,
			observationCategory: d.observationCode.split("_")[0]?.toUpperCase(),
		})) ?? [];

	// TS1-v3: N/A / Incomplete maneuver ratings must not exclude OBs from A/B.
	const maneuversCodeToExclude = buildTs1V3ManeuverCodesToExclude({
		candidateId,
		preferredRole,
		assessmentConfig: maneuvers,
		maneuverOutcomes,
		candidateIndex,
		hasSecondCandidate,
		temRate: rates,
	});

	const lastResetTimeMap = new Map<string, number>();
	for (const rm of resetManeuvers) {
		if (rm.candidateId !== candidateId) continue;
		const ts = new Date(rm.createdAt).getTime();
		const prev = lastResetTimeMap.get(rm.maneuverCode) ?? 0;
		if (ts > prev) lastResetTimeMap.set(rm.maneuverCode, ts);
	}

	const lastObResetTimeMap = buildLastObResetTimeMap(addRemoveOb, candidateId);

	const filteredAddRemoveOb = filterAddRemoveObForRating(candidateAddRemoveOb, candidateId, lastObResetTimeMap)
		.filter((d) => {
			const lastReset = lastResetTimeMap.get(d.maneuverCode);
			if (!lastReset) return true;
			return new Date(d.createdAt).getTime() >= lastReset;
		});

	const naRoleKeys = buildTs1V3NaRoleObKeys(filteredAddRemoveOb);
	const predefinedObs: IObservationRating[] = [];
	for (const assessmentConfig of maneuvers) {
		for (const d of assessmentConfig.observations ?? []) {
			if (
				isTs1V3ConfigObsExcludedByNa({
					maneuverCode: assessmentConfig.maneuverType.code,
					observationCode: d.code,
					candidateRole: d.candidateRole,
					naRoleKeys,
				})
			) {
				continue;
			}
			predefinedObs.push({
				observationCode: d.code,
				maneuverCode: assessmentConfig.maneuverType.code,
				totalAttempt: d.attempt,
				activeAttempt: d.attempt,
				isRemoved: false,
				candidateRole: d.candidateRole ?? null,
			});
		}
	}

	const uniqueCandidateAddRemoveOb = mergeTs1V3ObEventsForRatingCalc(filteredAddRemoveOb);

	const excludeSet = [...new Set(maneuversCodeToExclude)];

	const disabledObKeys = buildTs1V3FullyDisabledObKeys(filteredAddRemoveOb);

	const orderedActiveObsChronological: IObservationRating[] = filterAddRemoveObForRating(
		candidateAddRemoveOb,
		candidateId,
		lastObResetTimeMap,
	)
		.filter((d) => {
			const lastReset = lastResetTimeMap.get(d.maneuverCode);
			if (!lastReset) return true;
			return new Date(d.createdAt).getTime() >= lastReset;
		})
		.filter((d) => !excludeSet.includes(d.maneuverCode))
		.filter((d) => !disabledObKeys.has(`${d.maneuverCode}::${d.observationCode}`))
		.filter((d) => !d.isDisabled)
		// Drop prior events for N/A'd roles so UI-counter orphans cannot inflate How Often (e.g. 2/3→B=2).
		.filter(
			(d) =>
				!isTs1V3ConfigObsExcludedByNa({
					maneuverCode: d.maneuverCode,
					observationCode: d.observationCode,
					candidateRole: d.candidateRole,
					naRoleKeys,
				}),
		)
		.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
		.map((d) => ({
			isRemoved: d.isRemoved ?? false,
			observationCode: d.observationCode,
			maneuverCode: d.maneuverCode,
			totalAttempt: d.totalAttempt ?? 1,
			activeAttempt: d.activeAttempt ?? 1,
			candidateRole: d.candidateRole ?? null,
		}));

	const activeObs: IObservationRating[] = [];
	const disabledObs: IPreviewDisabledObservation[] = [];

	for (const d of uniqueCandidateAddRemoveOb) {
		if (!includeTs1V3ObInRatingNewObs(d)) continue;

		activeObs.push({
			isRemoved: d.isRemoved ?? false,
			observationCode: d.observationCode,
			maneuverCode: d.maneuverCode,
			totalAttempt: d.totalAttempt ?? 1,
			activeAttempt: d.activeAttempt ?? 1,
			candidateRole: d.candidateRole ?? null,
		});
	}

	for (const key of disabledObKeys) {
		const sep = key.indexOf("::");
		if (sep < 0) continue;
		disabledObs.push({
			maneuverCode: key.slice(0, sep),
			observationCode: key.slice(sep + 2),
			maneuverId: "",
			observationId: "",
			candidateId,
		});
	}

	const previewCalculator = resolveStagingPreviewCalculator(assessment.ratingFormVersion);
	const v4OriginalObsCodes =
		resolveTs1RatingCalcKey(assessment.ratingFormVersion) === "v4"
			? resolveOriginalObsCodesFromAssessmentConfig(maneuvers)
			: undefined;

	const howManyCalculation = previewCalculator.ObservationBreakdownHowManyCalculation({
		newObs: activeObs,
		predefinedObs,
		maneuverCodeToExclude: excludeSet,
		disabledObservations: disabledObs,
		formTypeCode: CONSTANT.FORM_TYPE.ts_1,
		originalObsCodes: v4OriginalObsCodes,
	});

	const howOftenCalculation = previewCalculator.ObservationBreakdownHowOftenCalculation({
		newObs: activeObs,
		predefinedObs,
		maneuverCodeToExclude: excludeSet,
		disabledObservations: disabledObs,
		formTypeCode: CONSTANT.FORM_TYPE.ts_1,
		ratingAByCategory: new Map(
			(howManyCalculation ?? []).map(({ obCategoryCode, rating }) => [obCategoryCode, rating]),
		),
		orderedNewObs: orderedActiveObsChronological,
		originalObsCodes: v4OriginalObsCodes,
	});

	const categoryOrder = Array.from(
		new Set([
			...(howManyCalculation?.map((x) => x.obCategoryCode) ?? []),
			...(howOftenCalculation?.map((x) => x.obCategoryCode) ?? []),
		]),
	).sort((a, b) => a.localeCompare(b));

	const howManyByCompetency = new Map(
		(howManyCalculation ?? []).map((x) => [x.obCategoryCode, x.rating]),
	);
	const howOftenByCompetency = new Map(
		(howOftenCalculation ?? []).map((x) => [x.obCategoryCode, x.rating]),
	);
	const obApplicable = buildTs1ApplicableCountsFromAddRemoveOb(uniqueCandidateAddRemoveOb);
	const applicableByCompetency = new Map(
		TS1_REVIEW_COMPETENCY_ORDER.map((code) => [
			code,
			(obApplicable.get(code) ?? 0) > 0 || categoryOrder.includes(code) ? 1 : 0,
		]),
	);

	return { howManyByCompetency, howOftenByCompetency, applicableByCompetency, categoryOrder };
}

function buildChecklistSignOffForm(
	fb: UntypedFormBuilder,
	detail: ChecklistStagingDetail,
): UntypedFormArray {
	const container = createSignOffFormContainer(fb);
	const signOffArray = container.get("signOffForm") as UntypedFormArray;
	const assessment = detail.assessment;
	const signOff =
		(detail.signOffDetail as CandidateSignOffDetails | undefined) ??
		(assessment?.signOffDetails?.candidateSignOffDetails?.[detail.candidateId] as
			| CandidateSignOffDetails
			| undefined);
	const candidates = asStagingCandidates(assessment?.trainingInformation?.candidates ?? []);
	const { candidate } = resolveStagingCandidate(candidates, detail);

	const form = createCandidateSignoffForm(CONSTANT.FORM_TYPE.ts_1, fb, {
		candidateName: candidate?.name ?? "",
		candidateId: detail.candidateId,
		traineeAssessmentId: detail.trainingAssessmentId,
		trainerName: signOff?.trainerName ?? detail.instructorName ?? "",
		signOffDetails: {
			ratingA: signOff?.ratingA ?? 0,
			ratingB: signOff?.ratingB ?? 0,
			ratingC: signOff?.ratingC ?? 0,
			finalRating: signOff?.finalRating ?? 0,
			category: signOff?.category ?? "Training",
			sessionCheck: toRecommendationBool(signOff?.sessionCheck) as unknown as string | undefined,
			objectiveCheck: toRecommendationBool(signOff?.objectiveCheck) as unknown as string | undefined,
			clearanceCheck: signOff?.clearanceCheck,
			traineeName: signOff?.traineeName ?? candidate?.name ?? "",
			trainerName: signOff?.trainerName ?? detail.instructorName ?? "",
			generalObservation: signOff?.generalObservation ?? "",
			reminderNotes: signOff?.reminderNotes ?? "",
		},
	});

	signOffArray.push(form, { emitEvent: false });
	return signOffArray;
}

export function buildChecklistPreviewCandidateDetails(
	detail: ChecklistStagingDetail,
): Ts1PreviewCandidateDetails | null {
	const assessment = detail.assessment;
	if (!assessment) return null;
	const candidates = asStagingCandidates(assessment.trainingInformation?.candidates ?? []);
	const { candidate } = resolveStagingCandidate(candidates, detail);
	if (!candidate) return null;

	const sessionConfigurations = buildSessionConfigurationsFromStagingCandidate(
		candidate,
		assessment.formDetails?.sessionDetails ?? [],
	);
	const fs = candidate.flightSimulatorDetails ?? {};

	return {
		trainingDate: formatPreviewDate(assessment.trainingInformation?.assessmentDate),
		sessionConfigurations,
		name: (candidate.name ?? "-").toString(),
		rank: displayOption(candidate.rank),
		staffNumber: (candidate.staffNumber ?? "-").toString(),
		pilotProfile: displayOption(candidate.trainingProfile),
		licenseNo: (candidate.licenseNumber ?? "-").toString(),
		licenseType: displayOption(candidate.licenseType),
		licenseExpiryDate: candidate.licenseExpiryDate
			? formatPreviewDate(candidate.licenseExpiryDate, CONSTANT.DATE_FORMAT.DISPLAY_DATE_FORMAT)
			: "-",
		flightSimulatorDetails: buildFlightSimulatorDetailsPayload(fs as Record<string, unknown>),
	};
}

export function buildChecklistPreviewContext(
	detail: ChecklistStagingDetail,
	fb: UntypedFormBuilder,
): {
	candidateNames: string[];
	candidateIds: string[];
	candidateCrewRoles: CandidateRole[];
	maneuverOutcomes: ManeuverOutcomeDraft[];
	temCompetencyOutcomes: TemCompetencyOutcomeDraft[];
	competencyBlocks: Ts1ReviewCompetencyBlock[];
	maneuverRows: Ts1ReviewManeuverRow[];
	summaryComments: OBComments[];
	candidateDetails: Ts1PreviewCandidateDetails | null;
	signOffForm: UntypedFormArray;
	reminderNotesEnabled?: boolean;
} {
	const assessment = detail.assessment;
	if (!assessment) {
		const signOffForm = createSignOffFormContainer(fb).get("signOffForm") as UntypedFormArray;
		return {
			candidateNames: [],
			candidateIds: [],
			candidateCrewRoles: [],
			maneuverOutcomes: [],
			temCompetencyOutcomes: [],
			competencyBlocks: [],
			maneuverRows: [],
			summaryComments: [],
			candidateDetails: null,
			signOffForm,
		};
	}
	const candidates = asStagingCandidates(assessment.trainingInformation?.candidates ?? []);
	const { candidate, candidateIndex } = resolveStagingCandidate(candidates, detail);
	const cr = assessment.candidateRating ?? {};
	const selectedOBs = (cr.selectedOBs ?? []) as OBComments[];
	const restoredOutcomes = restoreV3OutcomesFromSelectedOBs(selectedOBs);
	const maneuverOutcomes = (
		cr.maneuverOutcomes?.length ? cr.maneuverOutcomes : restoredOutcomes.maneuverOutcomes
	) as ManeuverOutcomeDraft[];
	const temCompetencyOutcomes = normalizeTemCompetencyOutcomes(
		(
			cr.temCompetencyOutcomes?.length ? cr.temCompetencyOutcomes : restoredOutcomes.temCompetencyOutcomes
		) as TemCompetencyOutcomeDraft[],
		Math.max(candidates.length, 1),
	);
	const allCrewRoles = resolveStagingCrewRoles(candidates, cr.crewRoleAssignment, selectedOBs);
	const candidateCrewRoles = candidate ? [allCrewRoles[candidateIndex] ?? CandidateRole.PF] : [];
	const assessmentConfig = (assessment.formDetails?.assessmentConfig ?? []).filter(
		(item) => !!item?.maneuverType?.code,
	);

	const radarMetrics = computeStagingRadarMetrics(assessment, detail.candidateId, {
		candidateIndex,
		hasSecondCandidate: candidates.length >= 2,
		preferredRole: allCrewRoles[candidateIndex] ?? CandidateRole.PF,
		maneuverOutcomes,
	});
	const competencyBlocks = buildTs1ReviewCompetencyBlocks({
		howManyByCompetency: radarMetrics.howManyByCompetency,
		howOftenByCompetency: radarMetrics.howOftenByCompetency,
		applicableByCompetency: radarMetrics.applicableByCompetency,
		temCompetencyOutcomes,
		candidateIndex,
		hasSecondCandidate: candidates.length >= 2,
	});

	const maneuverRows = buildTs1ReviewManeuverRows({
		assessmentConfig,
		maneuverOutcomes,
		currentIndex: candidateIndex,
		hasSecondCandidate: candidates.length >= 2,
	});

	const summaryComments = selectedOBs.filter(
		(comment) => !comment.candidateId || comment.candidateId === detail.candidateId,
	);

	return {
		candidateNames: [candidate?.name ?? ""],
		candidateIds: [detail.candidateId],
		candidateCrewRoles,
		maneuverOutcomes,
		temCompetencyOutcomes,
		competencyBlocks,
		maneuverRows,
		summaryComments,
		candidateDetails: buildChecklistPreviewCandidateDetails(detail),
		signOffForm: buildChecklistSignOffForm(fb, detail),
		reminderNotesEnabled: !!assessment.formDetails?.reminderNotesEnabled,
	};
}
